import React, { useMemo, useState } from 'react';
import { Loader2, Upload, X, FileText } from 'lucide-react';
import { FormsApi } from '../services/formsApi';
import { FormConfig, FormField, FormResponses } from '../types/form.types';
import { FormTheme } from '../types/formTheme';

interface FormFileUploadProps {
    formId: string;
    field: FormField;
    value: string | number | string[];
    responses: FormResponses;
    fileFieldIds: string[];
    formConfig?: FormConfig;
    onChange: (value: string | string[]) => void;
    onUploadStateChange?: (fieldId: string, isUploading: boolean) => void;
    appearance?: FormTheme;
    className?: string;
}

interface UploadedFileMeta {
    name: string;
    url?: string;
    isImage: boolean;
}

const DEFAULT_MAX_FILES_PER_FIELD = 5;
const DEFAULT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const normalizeFileIds = (value: string | number | string[] | undefined): string[] => {
    if (Array.isArray(value)) {
        return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
    }

    if (typeof value === 'string' && value.trim().length > 0) {
        return [value.trim()];
    }

    return [];
};

const formatFileSize = (bytes: number): string => {
    if (bytes >= 1024 * 1024) {
        return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    }
    return `${Math.ceil(bytes / 1024)}KB`;
};

const FormFileUpload: React.FC<FormFileUploadProps> = ({
    formId,
    field,
    value,
    responses,
    fileFieldIds,
    formConfig,
    onChange,
    onUploadStateChange,
    appearance = 'default',
    className = ''
}) => {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string>('');
    const [deletingFileIds, setDeletingFileIds] = useState<string[]>([]);
    const [uploadedMetaById, setUploadedMetaById] = useState<Record<string, UploadedFileMeta>>({});

    const isMultiple = Boolean(field.multiple);
    const fileIds = normalizeFileIds(value);
    const fieldAllowedTypes = field.validation?.allowedTypes || [];
    const globalAllowedTypes = formConfig?.allowedFileTypes || [];
    const acceptedTypes = fieldAllowedTypes.length > 0 ? fieldAllowedTypes : globalAllowedTypes;
    const maxFileSize = field.validation?.maxFileSize ?? DEFAULT_MAX_FILE_SIZE_BYTES;
    const maxFilesPerField = field.validation?.maxFiles ?? (isMultiple ? DEFAULT_MAX_FILES_PER_FIELD : 1);
    const maxFileUploadsAcrossForm = formConfig?.maxFileUploads;

    const totalCurrentUploads = useMemo(() => {
        return fileFieldIds.reduce((total, fileFieldId) => {
            return total + normalizeFileIds(responses[fileFieldId]).length;
        }, 0);
    }, [fileFieldIds, responses]);

    const remainingFieldSlots = isMultiple
        ? Math.max(0, maxFilesPerField - fileIds.length)
        : 1;
    const remainingGlobalSlots = typeof maxFileUploadsAcrossForm === 'number'
        ? Math.max(0, maxFileUploadsAcrossForm - totalCurrentUploads)
        : null;

    const canUploadMore = isMultiple
        ? remainingFieldSlots > 0 && (remainingGlobalSlots === null || remainingGlobalSlots > 0)
        : fileIds.length > 0 || (remainingGlobalSlots === null || remainingGlobalSlots > 0);

    const setUploadingState = (nextValue: boolean) => {
        setIsUploading(nextValue);
        onUploadStateChange?.(field.fieldId, nextValue);
    };

    const applyFileValue = (nextIds: string[]) => {
        if (isMultiple) {
            onChange(nextIds);
            return;
        }
        onChange(nextIds[0] || '');
    };

    const validateSelectedFiles = (selectedFiles: File[]): string | null => {
        if (!isMultiple && selectedFiles.length > 1) {
            return 'Only one file can be uploaded for this field.';
        }

        const existingFieldCount = isMultiple ? fileIds.length : 0;
        if (existingFieldCount + selectedFiles.length > maxFilesPerField) {
            return `Maximum ${maxFilesPerField} file${maxFilesPerField === 1 ? '' : 's'} allowed for this field.`;
        }

        const totalExcludingCurrentField = totalCurrentUploads - fileIds.length;
        const resultingTotalUploads = totalExcludingCurrentField + existingFieldCount + selectedFiles.length;
        if (typeof maxFileUploadsAcrossForm === 'number' && resultingTotalUploads > maxFileUploadsAcrossForm) {
            return `You can upload up to ${maxFileUploadsAcrossForm} file${maxFileUploadsAcrossForm === 1 ? '' : 's'} in this form.`;
        }

        for (const file of selectedFiles) {
            if (file.size > maxFileSize) {
                return `${file.name} is too large. Maximum size is ${formatFileSize(maxFileSize)}.`;
            }

            if (fieldAllowedTypes.length > 0 && !fieldAllowedTypes.includes(file.type)) {
                return `${file.name} is not an accepted file type for this field.`;
            }

            if (globalAllowedTypes.length > 0 && !globalAllowedTypes.includes(file.type)) {
                return `${file.name} is not an accepted file type for this form.`;
            }
        }

        return null;
    };

    const handleFilesSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files || []);
        event.target.value = '';
        setUploadError('');

        if (selectedFiles.length === 0) {
            return;
        }

        const validationError = validateSelectedFiles(selectedFiles);
        if (validationError) {
            setUploadError(validationError);
            return;
        }

        setUploadingState(true);

        try {
            const baseFileIds = isMultiple ? fileIds : [];
            const newFileIds: string[] = [];
            const uploadedMeta: Record<string, UploadedFileMeta> = {};

            for (const file of selectedFiles) {
                const result = await FormsApi.uploadFile(formId, field.fieldId, file);
                newFileIds.push(result.fileId);
                uploadedMeta[result.fileId] = {
                    name: file.name,
                    url: result.publicUrl,
                    isImage: file.type.startsWith('image/')
                };
            }

            setUploadedMetaById((previous) => ({
                ...previous,
                ...uploadedMeta
            }));
            applyFileValue([...baseFileIds, ...newFileIds]);
        } catch (error) {
            setUploadError(error instanceof Error ? error.message : 'File upload failed.');
        } finally {
            setUploadingState(false);
        }
    };

    const handleRemoveFile = async (fileIdToRemove: string) => {
        if (deletingFileIds.includes(fileIdToRemove)) {
            return;
        }

        setUploadError('');
        setDeletingFileIds((previous) => [...previous, fileIdToRemove]);

        try {
            await FormsApi.deleteFile(formId, fileIdToRemove);
            const nextFileIds = fileIds.filter((fileId) => fileId !== fileIdToRemove);
            applyFileValue(nextFileIds);
            setUploadedMetaById((previous) => {
                const { [fileIdToRemove]: _, ...remaining } = previous;
                return remaining;
            });
        } catch (error) {
            setUploadError(error instanceof Error ? error.message : 'Failed to delete file.');
        } finally {
            setDeletingFileIds((previous) => previous.filter((fileId) => fileId !== fileIdToRemove));
        }
    };

    const surfaceClassName = appearance === 'tracking'
        ? 'rounded-lg border border-gray-300 bg-white'
        : 'rounded-xl border-2 border-gray-200 bg-white/80 backdrop-blur-sm';

    const controlClassName = appearance === 'tracking'
        ? 'rounded-lg border border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'
        : 'rounded-xl border-2 border-gray-200 bg-white text-gray-700 hover:border-gray-300';

    return (
        <div className={`space-y-3 ${className}`}>
            <div className={`p-4 ${surfaceClassName}`}>
                <label
                    htmlFor={`${field.fieldId}-upload`}
                    className={`inline-flex items-center gap-2 px-4 py-2.5 font-medium transition-colors ${controlClassName} ${isUploading || !canUploadMore ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                >
                    {isUploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    <span>{isUploading ? 'Uploading...' : isMultiple ? 'Add files' : 'Upload file'}</span>
                </label>
                <input
                    id={`${field.fieldId}-upload`}
                    type="file"
                    className="sr-only"
                    multiple={isMultiple}
                    accept={acceptedTypes.length > 0 ? acceptedTypes.join(',') : undefined}
                    onChange={handleFilesSelected}
                    disabled={isUploading || !canUploadMore}
                />

                <div className="mt-3 text-xs text-gray-500 space-y-1">
                    <p>Max size per file: {formatFileSize(maxFileSize)}</p>
                    <p>Field limit: {maxFilesPerField} file{maxFilesPerField === 1 ? '' : 's'}</p>
                    {typeof maxFileUploadsAcrossForm === 'number' && (
                        <p>Form limit: {maxFileUploadsAcrossForm} file{maxFileUploadsAcrossForm === 1 ? '' : 's'}</p>
                    )}
                    {acceptedTypes.length > 0 && (
                        <p>Accepted: {acceptedTypes.join(', ')}</p>
                    )}
                </div>
            </div>

            {fileIds.length > 0 && (
                <ul className="space-y-2">
                    {fileIds.map((fileId) => {
                        const fileMeta = uploadedMetaById[fileId];
                        const fileName = fileMeta?.name || `Uploaded file (${fileId})`;
                        const isDeleting = deletingFileIds.includes(fileId);
                        return (
                            <li
                                key={fileId}
                                className={`flex items-center justify-between gap-3 p-3 ${surfaceClassName}`}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    {fileMeta?.isImage && fileMeta.url ? (
                                        <img src={fileMeta.url} alt={fileName} className="h-10 w-10 rounded object-cover border border-gray-200" />
                                    ) : (
                                        <span className="inline-flex h-10 w-10 items-center justify-center rounded bg-gray-100 text-gray-500">
                                            <FileText size={16} />
                                        </span>
                                    )}
                                    <span className="text-sm text-gray-700 truncate">{fileName}</span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleRemoveFile(fileId)}
                                    className="inline-flex items-center justify-center h-8 w-8 rounded text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    aria-label={`Remove ${fileName}`}
                                    disabled={isUploading || isDeleting}
                                >
                                    {isDeleting ? <Loader2 size={16} className="animate-spin" /> : <X size={16} />}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {uploadError && (
                <div className="text-sm text-red-600" role="alert">
                    {uploadError}
                </div>
            )}
        </div>
    );
};

export default FormFileUpload;
